<template>
    <nav
        class="sidebar-navigation"
        role="navigation"
        :aria-label="$t('navigation.mainNavigation')"
    >
        <!-- Navigation Categories -->

        <div
            v-for="(category, index) in navigationItems"
            :key="index"
            class="navigation-category"
            :class="{ 'align-items-center': isCompact }"
            data-testid="nav-category"
        >
            <!-- Category Title -->
            <p class="category-title text-muted m-0" :aria-hidden="isCompact">
                {{ $t(category.labelKey) }}
            </p>

            <!-- Navigation Items List -->
            <ul
                class="navigation-list p-0 m-0 d-flex flex-column"
                :aria-label="$t(category.labelKey)"
            >
                <template v-for="item in category.items" :key="item.name">
                    <li
                        class="nav-item"
                        :class="{
                            active: isActive(item) && !hasActiveChild(item),
                            'coming-soon': item.comingSoon,
                        }"
                    >
                        <div
                            :to="item.link"
                            class="router-link d-flex align-items-center"
                            @click.prevent="handleClick(item)"
                        >
                            <!-- Navigations items icons -->
                            <i
                                v-if="item.icon"
                                :class="[
                                    'fi nav-icons',
                                    item.comingSoon ? 'fi-clock' : item.icon,
                                ]"
                            ></i>
                            <!-- Navigation item text -->
                            <span
                                v-if="!isCompact"
                                class="d-flex align-items-center gap-2"
                            >
                                {{ $t(item.labelKey) }}
                            </span>
                            <!-- 
                                Renders an urgent notification icon conditionally based on a boolean flag from hasUrgentNotification method.
                            -->
                            <i
                                v-if="hasUrgentNotification(item.module)"
                                class="fi fi-alert-circle urgent-alert"
                                :class="{ 'urgent-alert-compact': isCompact }"
                            ></i>
                        </div>
                        <button
                            v-if="showChildren(item)"
                            type="button"
                            class="nav-group-toggle"
                            :aria-expanded="String(isGroupOpen(item))"
                            :aria-controls="groupId(item)"
                            :aria-label="
                                $t(
                                    isGroupOpen(item)
                                        ? 'appShell.sidebar.navigation.hideGroup'
                                        : 'appShell.sidebar.navigation.showGroup',
                                    { name: $t(item.labelKey) }
                                )
                            "
                            @click="toggleGroup(item)"
                        >
                            <i
                                :class="[
                                    'fi',
                                    isGroupOpen(item)
                                        ? 'fi-chevron-up'
                                        : 'fi-chevron-down',
                                ]"
                                aria-hidden="true"
                            ></i>
                        </button>
                    </li>
                    <li
                        v-if="showChildren(item) && isGroupOpen(item)"
                        class="nav-subgroup"
                    >
                        <ul
                            :id="groupId(item)"
                            class="navigation-sublist p-0 m-0 d-flex flex-column"
                            :aria-label="$t(item.labelKey)"
                        >
                            <li
                                v-for="child in item.children"
                                :key="child.name"
                                class="nav-item nav-item--child"
                                :class="{ active: isActive(child) }"
                            >
                                <router-link
                                    :to="child.link"
                                    class="router-link d-flex align-items-center"
                                    :aria-current="
                                        isActive(child) ? 'page' : null
                                    "
                                    @click="$emit('link-clicked')"
                                >
                                    <span>{{ $t(child.labelKey) }}</span>
                                    <template v-if="child.locked">
                                        <i
                                            class="fi fi-lock nav-lock"
                                            aria-hidden="true"
                                        ></i>
                                        <span class="visually-hidden">
                                            {{
                                                $t(
                                                    'appShell.sidebar.navigation.locked'
                                                )
                                            }}
                                        </span>
                                    </template>
                                </router-link>
                            </li>
                        </ul>
                    </li>
                </template>
            </ul>

            <!-- Category Separator -->
            <div
                v-if="index === 0"
                class="separator-line w-100"
                role="separator"
            ></div>
        </div>
    </nav>
</template>

<script>
import { useNotificationStore } from '@/stores/notification';

// The app remembers a closed group on this device. Groups are open by default.
const CLOSED_GROUPS_KEY = 'sidebarClosedGroups';
const ABX_PATH = '/program/abx-stewardship';

const readClosedGroups = () => {
    try {
        const value = JSON.parse(localStorage.getItem(CLOSED_GROUPS_KEY));
        return Array.isArray(value) ? value : [];
    } catch {
        return [];
    }
};

export default {
    name: 'SideBarNavigation',

    props: {
        isCompact: {
            type: Boolean,
            required: true,
            default: false,
        },
    },

    emits: ['link-clicked'],

    data() {
        return {
            urgentModules: [], // initial value to set to empty array
            notificationStore: useNotificationStore(), // notification store instance
            closedGroups: readClosedGroups(),
        };
    },
    computed: {
        // The server site list decides the lock and the target: All my sites for 2 or more sites.
        antimicrobialsItem() {
            const store = this.$abxCompanyStore;
            const locked = store.sitesLoaded && !store.hasAbxAccess;
            return {
                name: 'Antimicrobials',
                labelKey: 'appShell.sidebar.navigation.antimicrobials',
                locked,
                link: locked
                    ? { name: 'Program', query: { request: 'abx-stewardship' } }
                    : store.isMultiSite
                    ? { name: 'ABX All Sites' }
                    : ABX_PATH,
                activePaths: [ABX_PATH],
            };
        },
        abxContextKey() {
            const companyId =
                this.$supportStore.companyId ||
                this.$userStore.company?.id ||
                this.$userStore.user?.company_id;
            return this.$userStore.canUsePrograms()
                ? `${this.$userStore.user?.id}:${companyId}`
                : null;
        },
        navigationItems() {
            let workspaceItems = [
                {
                    name: 'Dashboard',
                    labelKey: 'appShell.sidebar.navigation.dashboard',
                    icon: 'fi-dashboard',
                    link: '/',
                },
            ];
            const canAccessAlphaSectorFeatures =
                this.$userStore.programEligibleSectorAlphaAccess();

            // Team
            if (this.$userStore.hasPermOneOf('team:read', 'team:write')) {
                workspaceItems.push({
                    name: 'Team',
                    labelKey: 'appShell.sidebar.navigation.team',
                    icon: 'fi-users',
                    link: '/team',
                    module: 'TEAM',
                    isAllowed: this.$userStore.companyHasPerm('team:read'),
                });
            }

            // Courses
            if (this.$userStore.hasPermOneOf('courses:read', 'courses:write')) {
                workspaceItems.push({
                    name: 'Education',
                    labelKey: 'appShell.sidebar.navigation.education',
                    icon: 'fi-course',
                    link: '/courses',
                    module: 'COURSES',
                    isAllowed: this.$userStore.companyHasPerm('courses:read'),
                });
            }

            // Audits
            if (this.$userStore.hasPermOneOf('audits:read', 'audits:write')) {
                workspaceItems.push({
                    name: 'Audits',
                    labelKey: 'appShell.sidebar.navigation.audits',
                    icon: 'fi-audit',
                    link: '/audits',
                    module: 'AUDITS',
                    isAllowed: this.$userStore.companyHasPerm('audits:read'),
                });
            }

            // Inspections
            if (
                this.$userStore.getCompanySectorName !== 'Clinical' &&
                this.$userStore.hasPermOneOf(
                    'inspection:read',
                    'inspection:write'
                )
            ) {
                workspaceItems.push({
                    name: 'Inspections',
                    labelKey: 'appShell.sidebar.navigation.inspections',
                    icon: 'fi-clipboard',
                    link: '/inspections',
                    module: 'INSPECTIONS',
                    isAllowed:
                        this.$userStore.companyHasPerm('inspection:read'),
                });
            }

            // Surveillance
            if (canAccessAlphaSectorFeatures) {
                workspaceItems.push({
                    name: 'Surveillance',
                    labelKey: 'appShell.sidebar.navigation.surveillance',
                    icon: 'fi-radar',
                    link: '/surveillance',
                    module: 'SURVEILLANCE',
                    isAllowed: canAccessAlphaSectorFeatures,
                });
                workspaceItems.push({
                    name: 'Residents',
                    labelKey: 'appShell.sidebar.navigation.residents',
                    icon: 'fi-users',
                    link: '/residents',
                    module: 'SURVEILLANCE',
                    isAllowed: canAccessAlphaSectorFeatures,
                });
            }

            // Outbreak
            if (canAccessAlphaSectorFeatures) {
                workspaceItems.push({
                    name: 'Outbreak',
                    labelKey: 'appShell.sidebar.navigation.outbreak',
                    icon: 'fi-outbreak-attention',
                    link: '/outbreak',
                    module: 'OUTBREAK',
                    isAllowed: canAccessAlphaSectorFeatures,
                });
            }

            // Program
            if (this.$userStore.canUsePrograms()) {
                workspaceItems.push({
                    name: 'Programs',
                    labelKey: 'appShell.sidebar.navigation.programs',
                    icon: 'fi-puzzle',
                    link: '/program',
                    module: 'PROGRAM',
                    isAllowed: true,
                    children: [this.antimicrobialsItem],
                });
            }

            // Analysis
            if (canAccessAlphaSectorFeatures) {
                workspaceItems.push({
                    name: 'Analysis',
                    labelKey: 'appShell.sidebar.navigation.analysis',
                    icon: 'fi-chart',
                    link: '/analysis',
                    module: 'ANALYSIS',
                    isAllowed: canAccessAlphaSectorFeatures,
                });
            }

            // Library
            if (this.$userStore.hasPermOneOf('library:read', 'library:write')) {
                workspaceItems.push({
                    name: 'Library',
                    labelKey: 'appShell.sidebar.navigation.library',
                    icon: 'fi-book',
                    link: '/library',
                    module: 'LIBRARY',
                    isAllowed: this.$userStore.companyHasPerm('library:read'),
                });
            }

            // Secondment
            if (this.$userStore.companyHasPerm('secondments:read')) {
                workspaceItems.push({
                    name: 'Secondment',
                    labelKey: 'appShell.sidebar.navigation.secondment',
                    icon: 'fi-mask',
                    link: '/secondment',
                    module: 'SECONDMENT',
                    isAllowed: this.$userStore.hasPermOneOf('secondments:read'),
                });
            }

            // Inventory - Only for users whose company has inventory access
            const hasInventoryAccess =
                this.$permStore.companyHasPerm('inventory:read') &&
                this.$userStore.hasPermOneOf(
                    'inventory:read',
                    'inventory:write'
                );

            workspaceItems.push({
                name: 'Inventory',
                labelKey: 'appShell.sidebar.navigation.inventory',
                icon: 'fi-inventory',
                link: '/inventory',
                module: 'INVENTORY',
                isAllowed: hasInventoryAccess,
            });

            // All users have access to all these items
            let generalItems = [
                {
                    name: 'Settings',
                    labelKey: 'appShell.sidebar.navigation.settings',
                    icon: 'fi-gear',
                    link: '/settings',
                },
                {
                    name: 'Help',
                    labelKey: 'appShell.sidebar.navigation.help',
                    icon: 'fi-help',
                    link: '/help',
                },
                {
                    name: 'Contact Us',
                    labelKey: 'appShell.sidebar.navigation.contactUs',
                    icon: 'fi-support',
                    link: '/contact-us',
                    module: 'SUPPORT',
                },
            ];

            // Admin Panel
            if (this.$userStore.isAppAdmin) {
                generalItems.unshift({
                    name: 'Admin',
                    labelKey: 'appShell.sidebar.navigation.admin',
                    icon: 'fi-star',
                    link: '/admin',
                });
            }

            // A support tab shows only the supported pages. Admin ends support mode.
            if (this.$supportStore.active) {
                const supportLinks = [
                    '/program',
                    '/residents',
                    '/surveillance',
                ];
                workspaceItems = workspaceItems.filter((item) =>
                    supportLinks.includes(item.link)
                );
                generalItems = generalItems.filter(
                    (item) => item.link === '/admin'
                );
            }

            return [
                {
                    name: 'Workspace',
                    labelKey: 'appShell.sidebar.categories.workspace',
                    items: workspaceItems,
                },
                {
                    name: 'General',
                    labelKey: 'appShell.sidebar.categories.general',
                    items: generalItems,
                },
            ];
        },
    },

    watch: {
        abxContextKey: {
            immediate: true,
            handler(key) {
                if (!key) return;
                this.$abxCompanyStore.syncSession();
                this.$abxCompanyStore.loadSites().catch(() => {
                    // Without the site list the link opens the workspace, which shows its own errors.
                });
            },
        },
        /**
         * Watch for changes in notification store's urgent modules
         * @param {Array} urgentModules - Array of module names with urgent notifications
         */
        'notificationStore.urgentModules': {
            immediate: true,
            handler(urgent_modules) {
                if (
                    Array.isArray(urgent_modules) &&
                    urgent_modules.length > 0
                ) {
                    this.urgentModules = urgent_modules;
                } else {
                    this.urgentModules = []; //["TEAM", "COURSES"] // set to test, make it empty list `[]` in production
                }
            },
        },
    },
    methods: {
        handleClick(item) {
            if (!('isAllowed' in item) || item.isAllowed) {
                if (item.comingSoon) {
                    this.$userStore.toggleComingSoonModal(true);
                    return;
                }
                this.$emit('link-clicked');
                this.$router.push(item.link);
            } else {
                // Check if company has access but user doesn't have permission
                if (
                    item.module === 'INVENTORY' &&
                    this.$permStore.companyHasPerm('inventory:read')
                ) {
                    // Company has Silver/Gold plan, user just needs permission
                    this.$userStore.togglePermissionDeniedModal(true);
                } else {
                    // Company doesn't have the plan, show upgrade modal
                    this.$userStore.togglePremiumModal(true);
                }
            }
        },

        /**
         * Checks if the navigation item matches the current route.
         * @param {{ link?: string, activePaths?: string[] }} item - Nav item
         * @returns {boolean}
         */
        isActive(item) {
            const paths = item.activePaths ?? (item.link ? [item.link] : []);
            const routePath = this.$route.path;
            return paths.some(
                (p) => routePath === p || routePath.startsWith(`${p}/`)
            );
        },
        showChildren(item) {
            return Boolean(item.children?.length) && !this.isCompact;
        },
        hasActiveChild(item) {
            return Boolean(
                this.showChildren(item) &&
                    item.children.some((child) => this.isActive(child))
            );
        },
        groupId(item) {
            return `nav-group-${item.name.toLowerCase()}`;
        },
        isGroupOpen(item) {
            return !this.closedGroups.includes(item.name);
        },
        toggleGroup(item) {
            this.closedGroups = this.isGroupOpen(item)
                ? [...this.closedGroups, item.name]
                : this.closedGroups.filter((name) => name !== item.name);
            try {
                localStorage.setItem(
                    CLOSED_GROUPS_KEY,
                    JSON.stringify(this.closedGroups)
                );
            } catch {
                // The group still toggles when storage is blocked.
            }
        },
        hasUrgentNotification(module) {
            return module && this.urgentModules?.includes(module);
        },
    },
};
</script>

<style scoped>
/* Navigation category styles */
.navigation-category {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
}

/* Category title */
.category-title {
    font-size: 0.875rem;
    line-height: 1.21;
    color: var(--text-muted);
}

/* Navigation list */
.navigation-list {
    gap: var(--space-1);
}

/* Navigation item */
.nav-item {
    display: flex;
    align-items: center;
    cursor: pointer;
    border-radius: var(--radius-sm);
    transition: background-color 0.3s ease-in-out;
}

/* Router link styling */
.router-link {
    gap: var(--space-3);
    text-decoration: none;
    position: relative;
    width: 100%;
    height: 100%;
    padding: var(--space-2);
    user-select: none;
}

/* Icon styling */
.nav-icons {
    width: 1.125rem;
    height: 1.125rem;
    font-size: 1.125rem;
    color: var(--primary-300);
    transition: color 0.3s ease-in-out;
}

/* Navigation item text */
.nav-item span {
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 0.9375rem;
    line-height: 1.2;
    color: var(--neutral-900);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: all 0.3s ease-in-out;
}

/* Coming soon / disabled state */
.nav-item.coming-soon {
    opacity: 0.6;
}

.nav-item.coming-soon:hover {
    background-color: transparent;
}

/* Expandable group: chevron button and child links */
.nav-group-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    margin-right: var(--space-1);
    padding: 0;
    border: 0;
    border-radius: var(--radius-sm);
    background: none;
    color: var(--neutral-800);
}

.nav-group-toggle:hover {
    background-color: var(--primary-50);
}

.nav-group-toggle:focus-visible,
.nav-item--child .router-link:focus-visible {
    outline: 2px solid var(--primary-500);
    outline-offset: -2px;
}

.navigation-sublist {
    gap: var(--space-1);
    list-style: none;
}

.nav-subgroup {
    list-style: none;
}

.nav-item--child .router-link {
    padding-left: calc(var(--space-2) + 1.125rem + var(--space-3));
}

.nav-lock {
    margin-left: auto;
    color: var(--neutral-800);
    font-size: 0.875rem;
}

/* Category separator */
.separator-line {
    border-bottom: 1px solid var(--neutral-600);
    margin: var(--space-2) 0 var(--space-4);
}

/* Active and hover states */
.nav-item:hover,
.nav-item.active {
    background-color: var(--primary-25);
}

.nav-item.active span {
    font-weight: 700;
}

.urgent-alert {
    color: var(--danger);
    width: 1.125rem;
    height: 1.125rem;
}

.urgent-alert-compact {
    position: absolute;
    top: 0.5rem;
    left: 1.625rem;
    transform: translateY(-50%);
    margin-left: 0;
    z-index: 2;
}

/* Mobile responsiveness */
@media only screen and (max-width: 767.98px) {
    .category-title {
        font-size: 0.75rem;
        line-height: 0.9375rem;
    }

    .router-link {
        gap: var(--space-2);
    }

    .nav-item span {
        font-size: 0.875rem;
        line-height: 1.0625rem;
    }
}
</style>
